// 動作歷史 GET /front/v1/report/:id/history 的請求狀態（報告改版工單 25 PR-B；回應形狀見後端工單 12、spec「動作歷史（公開）」）。
// 純函式（S2：tests/report-history.test.mjs「report history」）；stores/modules/report.js 使用（共用 chunk——只 import errors.js）。
//
// /history 是次要請求：任何失敗只讓比較列降級（PR-C 起：整槽不畫或顯示一句），**不得**擋整頁、不得彈整頁錯誤、不進重試計畫、
// 不上報（只有解密失敗沿用 store 既有的 CLIENT_DECRYPT 上報規則，同報告只報一次）。所以這裡只把結果記成狀態，不經
// errors.js 的 classifyOutcome（那是整頁錯誤畫面的分類）。
//
// 狀態 { status, data, reason, category }：
//   idle     還沒發（開關關、舊渲染器、或還沒輪到）
//   loading  進行中
//   ok       200 且 series 非空——data＝{ anchor, series, reason }（PR-C 交給 trend.logic.js 的 deriveTrends）
//   empty    200 且 series 為空——reason＝後端降級原因（ORG_ID_EMPTY／ANCHOR_NOT_FINISHED；未來新增的原因照字串保留），
//            沒有 reason＝錨點紀錄都沒有可用的 key（比較列隱藏）
//   failed   沒有可用回應——category＝回報碼類別段（errors.js reportCodeCategory）：HTTP404（端點尚未部署）、INTERNAL（5xx 帶
//            errorCode）、HTTP5xx、RATE_LIMITED（429）、NETWORK、REPORT_NOT_READY（400：錨點查無但 execution 存在／取不到動作清單）、
//            REPORT_NOT_FOUND、LINK_INVALID、CLIENT_DECRYPT、HTTP200（200 但形狀不對）
// data 一律 Object.freeze（只凍頂層）：Vue 2 不 observe 不可擴充的物件，整包（dev 實測 0.7 MB、ACLR 類估 2 MB）不會被深層加上
// getter／setter；內容唯讀，deriveTrends 是純函式不改它。
import { failureOutcome, reportCodeCategory } from './errors.js'

export const HISTORY_STATUS = Object.freeze({
  IDLE: 'idle',
  LOADING: 'loading',
  OK: 'ok',
  EMPTY: 'empty',
  FAILED: 'failed',
})

/** 後端降級原因（200＋series:[]＋reason；後端 go/src/contest/report/api_history.go，前端契約只增不改）。 */
export const HISTORY_REASON = Object.freeze({
  ORG_ID_EMPTY: 'ORG_ID_EMPTY',
  ANCHOR_NOT_FINISHED: 'ANCHOR_NOT_FINISHED',
})

const isObject = v => v !== null && typeof v === 'object' && !Array.isArray(v)
const strOrNull = v => (typeof v === 'string' && v.length > 0 ? v : null)

const state = (status, { data = null, reason = null, category = null } = {}) => ({ status, data, reason, category })

/** 初始（還沒發）。 */
export const initialHistoryState = () => state(HISTORY_STATUS.IDLE)

/** 進行中。 */
export const loadingHistoryState = () => state(HISTORY_STATUS.LOADING)

/**
 * 200 回應 body → 狀態（ok／empty）。形狀不對（不是物件、series 不是陣列）回 null——呼叫端當失敗（historyFailureState，類別段 HTTP200）。
 * @param {object} body  /history 解密後的回應
 */
export function historySuccessState (body) {
  if (!isObject(body) || !Array.isArray(body.series)) return null
  // 用簡寫屬性（不寫 `series:` 字面鍵）：tests/echarts-registration.test.mjs 把 src/report 下出現 `series:` 的 .js 當成組 echarts option 的檔
  const { series } = body
  const anchor = isObject(body.anchor) ? body.anchor : null
  const reason = strOrNull(body.reason)
  const data = Object.freeze({ anchor, series, reason })
  return state(series.length > 0 ? HISTORY_STATUS.OK : HISTORY_STATUS.EMPTY, { data, reason })
}

/**
 * 失敗結果（errors.js 的 failureOutcome；沒有就當無法歸類）→ failed 狀態，只記回報碼類別段。
 * @param {object|null} outcome
 */
export function historyFailureState (outcome) {
  const o = isObject(outcome) && outcome.ok === false ? outcome : failureOutcome({})
  return state(HISTORY_STATUS.FAILED, { category: reportCodeCategory(o) })
}

/**
 * 結果已確定、之後不必再自動重抓：ok，或 empty 且原因不是「錨點尚未完成」。
 * 用途（store 的 fetchHistory／頁殼）：跨頁殼載入的快取短路（同 token＋lang 有確定結果就不打）；主報告從「不能顯示」恢復時，
 * 不確定的（失敗、錨點尚未完成、還沒發）強制重抓一次——與主報告同時發出的 /history 在報告產生中會拿到 ANCHOR_NOT_FINISHED。
 */
export function historyIsFinal (history) {
  const h = isObject(history) ? history : {}
  if (h.status === HISTORY_STATUS.OK) return true
  return h.status === HISTORY_STATUS.EMPTY && h.reason !== HISTORY_REASON.ANCHOR_NOT_FINISHED
}
